import axios from 'axios';
import { toast } from 'sonner';

// Global interceptor: auto-unwrap paginated responses for ALL axios calls (including plain `import axios from 'axios'`)
// When backend returns {data: [...], total, page, limit}, this converts response.data to the array
// Pagination metadata is preserved in response.pagination
axios.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data) && Array.isArray(response.data.data)) {
      const { data: arrayData, ...paginationMeta } = response.data;
      response.pagination = paginationMeta;
      response.data = arrayData;
    }
    return response;
  },
  (error) => {
    // Handle subscription expired - redirect to purchase
    if (error.response?.status === 403 && error.response?.data?.error_code === 'SUBSCRIPTION_EXPIRED') {
      // Show alert and redirect
      if (!window.__subscriptionRedirecting) {
        window.__subscriptionRedirecting = true;
        alert('Your subscription/trial has expired. Please purchase a plan to continue.');
        window.location.href = '/purchase';
      }
    }
    return Promise.reject(error);
  }
);

// Use environment variable if set, otherwise use current origin (for production)
// Always ensure HTTPS for preview environments
let BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
// Force HTTPS for all API calls
if (BACKEND_URL.startsWith('http://') && !BACKEND_URL.includes('localhost')) {
  BACKEND_URL = BACKEND_URL.replace('http://', 'https://');
}
export const API_BASE = `${BACKEND_URL}/api`;

// Server status tracking
let serverDownToastId = null;
let isServerDown = false;
let lastHealthCheck = Date.now();

// Function to check server health
const checkServerHealth = async () => {
  try {
    const response = await fetch(`${API_BASE}/health`, { 
      method: 'GET',
      signal: AbortSignal.timeout(5000)
    });
    if (response.ok) {
      if (isServerDown) {
        isServerDown = false;
        if (serverDownToastId) {
          toast.dismiss(serverDownToastId);
          serverDownToastId = null;
        }
        toast.success('Connection restored! Server is back online.');
      }
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

// Show server down notification
const showServerDownNotification = () => {
  if (!isServerDown) {
    isServerDown = true;
    serverDownToastId = toast.error(
      'Server is temporarily unavailable. Please wait...',
      {
        duration: Infinity,
        id: 'server-down-toast',
        description: 'We are working to restore the connection.',
        action: {
          label: 'Retry',
          onClick: async () => {
            const isHealthy = await checkServerHealth();
            if (!isHealthy) {
              toast.error('Server is still unavailable. Please try again later.');
            }
          }
        }
      }
    );
  }
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to detect server errors AND auto-unwrap paginated responses
api.interceptors.response.use(
  (response) => {
    // Server is responding - clear any server down state
    if (isServerDown) {
      isServerDown = false;
      if (serverDownToastId) {
        toast.dismiss(serverDownToastId);
        serverDownToastId = null;
      }
      toast.success('Connection restored!');
    }

    // Auto-unwrap paginated responses: {data: [...], total, page, limit} → [...]
    // This ensures all components that expect arrays from response.data work correctly
    // Components needing pagination metadata can access response.pagination
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data) && Array.isArray(response.data.data)) {
      const { data: arrayData, ...paginationMeta } = response.data;
      response.pagination = paginationMeta;
      response.data = arrayData;
    }

    return response;
  },
  (error) => {
    // Check if it's a network error or server error (5xx)
    if (!error.response) {
      // Network error - server unreachable
      showServerDownNotification();
    } else if (error.response.status >= 500) {
      // Server error (500, 502, 503, etc.)
      showServerDownNotification();
    }
    
    return Promise.reject(error);
  }
);

// Periodic health check (every 30 seconds when tab is visible)
let healthCheckInterval = null;

const startHealthCheck = () => {
  if (healthCheckInterval) return;
  
  healthCheckInterval = setInterval(async () => {
    if (document.visibilityState === 'visible' && isServerDown) {
      await checkServerHealth();
    }
  }, 30000); // Check every 30 seconds
};

const stopHealthCheck = () => {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval);
    healthCheckInterval = null;
  }
};

// Start/stop health check based on tab visibility
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      startHealthCheck();
      if (isServerDown) {
        checkServerHealth();
      }
    } else {
      stopHealthCheck();
    }
  });
  
  // Start health check when page loads
  startHealthCheck();
}

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getChartData: () => api.get('/dashboard/chart-data'),
  getStudentDashboard: () => api.get('/dashboard/student-dashboard'),
  getParentDashboard: () => api.get('/dashboard/parent-dashboard'),
  getTeacherDashboard: () => api.get('/dashboard/teacher-dashboard'),
  getBirthdaysToday: () => api.get('/dashboard/birthdays-today'),
  getRecentNotices: () => api.get('/dashboard/recent-notices'),
  getFeeSummary: () => api.get('/dashboard/fee-summary'),
  getQuickStats: () => api.get('/dashboard/quick-stats'),
  sendBirthdayWish: (personId, personType, method = 'manual') => 
    api.post(`/dashboard/birthday-wish/send?person_id=${personId}&person_type=${personType}&method=${method}`),
  getTodayWishes: () => api.get('/dashboard/birthday-wishes/today'),
};

// Master Settings API
export const masterSettingsAPI = {
  // Courses
  getCourses: () => api.get('/master-settings/courses'),
  createCourse: (data) => api.post('/master-settings/courses', data),
  updateCourse: (id, data) => api.put(`/master-settings/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/master-settings/courses/${id}`),
  
  // Semesters
  getSemesters: (courseId) => api.get('/master-settings/semesters', courseId ? { params: { course_id: courseId } } : {}),
  createSemester: (data) => api.post('/master-settings/semesters', data),
  updateSemester: (id, data) => api.put(`/master-settings/semesters/${id}`, data),
  deleteSemester: (id) => api.delete(`/master-settings/semesters/${id}`),
  
  // Subjects
  getSubjects: (semesterId) => api.get('/master-settings/subjects', { params: { semester_id: semesterId } }),
  createSubject: (data) => api.post('/master-settings/subjects', data),
  
  // Sections
  getSections: () => api.get('/master-settings/sections'),
  createSection: (data) => api.post('/master-settings/sections', data),
  updateSection: (id, data) => api.put(`/master-settings/sections/${id}`, data),
  deleteSection: (id) => api.delete(`/master-settings/sections/${id}`),
  
  // Fee Structures
  getFeeStructures: () => api.get('/master-settings/fee-structures'),
  createFeeStructure: (data) => api.post('/master-settings/fee-structures', data),
  updateFeeStructure: (id, data) => api.put(`/master-settings/fee-structures/${id}`, data),
  deleteFeeStructure: (id) => api.delete(`/master-settings/fee-structures/${id}`),
  
  // Discounts
  getDiscounts: () => api.get('/master-settings/discounts'),
  createDiscount: (data) => api.post('/master-settings/discounts', data),
  updateDiscount: (id, data) => api.put(`/master-settings/discounts/${id}`, data),
  deleteDiscount: (id) => api.delete(`/master-settings/discounts/${id}`),
  
  // Scholarships
  getScholarships: () => api.get('/master-settings/scholarships'),
  createScholarship: (data) => api.post('/master-settings/scholarships', data),
  updateScholarship: (id, data) => api.put(`/master-settings/scholarships/${id}`, data),
  deleteScholarship: (id) => api.delete(`/master-settings/scholarships/${id}`),
  
  // Taxes
  getTaxes: () => api.get('/master-settings/taxes'),
  createTax: (data) => api.post('/master-settings/taxes', data),
  updateTax: (id, data) => api.put(`/master-settings/taxes/${id}`, data),
  deleteTax: (id) => api.delete(`/master-settings/taxes/${id}`),
  
  // Other Fees
  getOtherFees: (courseId, semesterId) => api.get('/master-settings/other-fees', { params: { course_id: courseId, semester_id: semesterId } }),
  getAllOtherFeeTypes: () => api.get('/other-fees/fee-types'),
  createOtherFee: (data) => api.post('/master-settings/other-fees', data),
  updateOtherFee: (id, data) => api.put(`/master-settings/other-fees/${id}`, data),
  deleteOtherFee: (id) => api.delete(`/master-settings/other-fees/${id}`),
  
  // Other Fee Assignments
  assignOtherFee: (data) => api.post('/other-fees/assign', data),
  getStudentOtherFees: (admissionNumber) => api.get(`/other-fees/assignments/student/${admissionNumber}`),
  payOtherFee: (assignmentId, paymentData) => api.post(`/other-fees/pay/${assignmentId}`, paymentData),
  
  // Fine Settings
  getFineSettings: () => api.get('/master-settings/fine-settings'),
  createFineSetting: (data) => api.post('/master-settings/fine-settings', data),
  updateFineSetting: (id, data) => api.put(`/master-settings/fine-settings/${id}`, data),
  deleteFineSetting: (id) => api.delete(`/master-settings/fine-settings/${id}`),
  
  // Sessions
  getSessions: () => api.get('/master-settings/sessions'),
  createSession: (data) => api.post('/master-settings/sessions', data),
  updateSession: (id, data) => api.put(`/master-settings/sessions/${id}`, data),
  deleteSession: (id) => api.delete(`/master-settings/sessions/${id}`),
  
  // Departments
  getDepartments: () => api.get('/master-settings/departments'),
  createDepartment: (data) => api.post('/master-settings/departments', data),
  updateDepartment: (id, data) => api.put(`/master-settings/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/master-settings/departments/${id}`),
  
  // Designations
  getDesignations: () => api.get('/master-settings/designations'),
  createDesignation: (data) => api.post('/master-settings/designations', data),
  updateDesignation: (id, data) => api.put(`/master-settings/designations/${id}`, data),
  deleteDesignation: (id) => api.delete(`/master-settings/designations/${id}`),
  
  // Attendance Fines
  getAttendanceFines: () => api.get('/master-settings/attendance-fines'),
  createAttendanceFine: (data) => api.post('/master-settings/attendance-fines', data),
  updateAttendanceFine: (id, data) => api.put(`/master-settings/attendance-fines/${id}`, data),
  deleteAttendanceFine: (id) => api.delete(`/master-settings/attendance-fines/${id}`),
  
  // Religions
  getReligions: () => api.get('/master-settings/religions'),
  createReligion: (data) => api.post('/master-settings/religions', data),
  updateReligion: (id, data) => api.put(`/master-settings/religions/${id}`, data),
  deleteReligion: (id) => api.delete(`/master-settings/religions/${id}`),
  
  // Castes
  getCastes: () => api.get('/master-settings/castes'),
  createCaste: (data) => api.post('/master-settings/castes', data),
  updateCaste: (id, data) => api.put(`/master-settings/castes/${id}`, data),
  deleteCaste: (id) => api.delete(`/master-settings/castes/${id}`),
};

// Students API
export const studentsAPI = {
  getStudents: (params) => api.get('/students/', { params }),
  getStudentsDropdown: (params) => api.get('/students/dropdown', { params }),
  getStudent: (id) => api.get(`/students/${id}`),
  createStudent: (data) => api.post('/students/', data),
  updateStudent: (id, data) => api.put(`/students/${id}`, data),
  blockStudent: (id) => api.post(`/students/${id}/block`),
  approveStudent: (id) => api.post(`/students/${id}/approve`),
  markAttendance: (id, data) => api.post(`/students/${id}/attendance`, data),
  getAttendance: (id, params) => api.get(`/students/${id}/attendance`, { params }),
  getLedger: (id, params) => api.get(`/students/${id}/ledger`, { params }),
};

// Fees API
export const feesAPI = {
  collectFee: (data) => api.post('/fees/payment', data),
  getPayments: (params) => api.get('/fees/payments', { params }),
  getStudentLedger: (studentId) => api.get(`/fees/ledger/${studentId}`),
  getDueFees: () => api.get('/fees/due-fees'),
  assignFee: (data) => api.post('/fees/assign-fee', data),
};

// Staff API
export const staffAPI = {
  getStaff: (params) => api.get('/staff/', { params }),
  getStaffMember: (id) => api.get(`/staff/${id}`),
  createStaff: (data) => api.post('/staff/', data),
  getNextEmployeeId: () => api.get('/staff/next-employee-id'),
  markAttendance: (id, data) => api.post(`/staff/${id}/attendance`, data),
  getAttendance: (id, params) => api.get(`/staff/${id}/attendance`, { params }),
  generatePayroll: (data) => api.post('/staff/generate-payroll', data),
  getPayroll: (params) => api.get('/staff/payroll/list', { params }),
};

// Library API
export const libraryAPI = {
  getBooks: (params) => api.get('/library/books', { params }),
  addBook: (data) => api.post('/library/books', data),
  issueBook: (data) => api.post('/library/issue', data),
  returnBook: (issueId) => api.post(`/library/return/${issueId}`),
  getIssuedBooks: (status) => api.get('/library/issued-books', { params: { status } }),
};

// Consultants API
export const consultantsAPI = {
  getConsultants: () => api.get('/consultants/'),
  getConsultant: (id) => api.get(`/consultants/${id}`),
  addConsultant: (data) => api.post('/consultants/', data),
  updateConsultant: (id, data) => api.put(`/consultants/${id}`, data),
  deleteConsultant: (id) => api.delete(`/consultants/${id}`),
  payConsultant: (data) => api.post('/consultants/payment', data),
  payConsultantStudentWise: (data) => api.post('/consultants/payment/student-wise', data),
  getConsultantLedger: (id) => api.get(`/consultants/${id}/ledger`),
  blockConsultant: (id) => api.post(`/consultants/${id}/block`),
  unblockConsultant: (id) => api.post(`/consultants/${id}/unblock`),
  getBusinessReport: () => api.get('/consultants/reports/business'),
  getStudentWisePayoutReport: (consultantId) => api.get(`/consultants/reports/student-wise-payout/${consultantId}`),
};

// Suppliers API
export const suppliersAPI = {
  getSuppliers: () => api.get('/inventory/suppliers'),
  getSupplier: (id) => api.get(`/inventory/suppliers/${id}`),
  addSupplier: (data) => api.post('/inventory/suppliers', data),
  updateSupplier: (id, data) => api.put(`/inventory/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/inventory/suppliers/${id}`),
};

// Seat Booking API
export const seatBookingAPI = {
  getBookings: (status) => api.get('/seat-booking/', { params: { status } }),
  bookSeat: (data) => api.post('/seat-booking/', data),
  convertToAdmission: (id, data) => api.post(`/seat-booking/${id}/convert-to-admission`, data),
  getReport: (params) => api.get('/seat-booking/report', { params }),
  getBookingAmountSettings: () => api.get('/seat-booking/amount-settings'),
  createBookingAmountSetting: (data) => api.post('/seat-booking/amount-settings', data),
};

// Admission Fee Settings API
export const admissionFeeAPI = {
  getAdmissionFees: () => api.get('/admission-fee-settings/'),
  getAdmissionFeeByCourse: (courseId, sessionId) => api.get(`/admission-fee-settings/course/${courseId}`, { params: { session_id: sessionId } }),
  createAdmissionFee: (data) => api.post('/admission-fee-settings/', data),
  createAdmissionFeeSetting: (data) => api.post('/admission-fee-settings/', data),
  updateAdmissionFee: (id, data) => api.put(`/admission-fee-settings/${id}`, data),
  deleteAdmissionFee: (id) => api.delete(`/admission-fee-settings/${id}`),
};

// College Settings API (Tenant Admin)
export const collegeSettingsAPI = {
  getSettings: () => api.get('/tenants/my-college/settings'),
  updateSettings: (data) => api.put('/tenants/my-college/settings', data),
};

// Tenants API (SuperAdmin & Module Access)
export const tenantsAPI = {
  // Tenant CRUD
  getTenants: () => api.get('/tenants/'),
  getTenant: (id) => api.get(`/tenants/${id}`),
  createTenant: (data) => api.post('/tenants/', data),
  updateTenant: (id, data) => api.put(`/tenants/${id}`, data),
  deleteTenant: (id) => api.delete(`/tenants/${id}`),
  resetAdminPassword: (id, data) => api.post(`/tenants/${id}/reset-admin-password`, data),
  
  // Module Access Control
  getAvailableModules: () => api.get('/tenants/modules/available'),
  getTenantModules: (tenantId) => api.get(`/tenants/${tenantId}/modules`),
  updateTenantModules: (tenantId, data) => api.put(`/tenants/${tenantId}/modules`, data),
  getMyModules: () => api.get('/tenants/my-modules'),
  
  // Subscription Plans
  getSubscriptionPlans: () => api.get('/tenants/subscription-plans/list'),
  updateSubscriptionPlan: (id, data) => api.put(`/tenants/subscription-plans/${id}`, data),
};

// Data Migration API (SuperAdmin)
export const dataMigrationAPI = {
  exportAll: () => api.get('/data-migration/export'),
  exportPlansOnly: () => api.get('/data-migration/export/plans-only'),
  exportTenantsOnly: () => api.get('/data-migration/export/tenants-only'),
  importData: (data) => api.post('/data-migration/import', data),
};

export default api;
